import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  HamburgerIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Flex,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Switch,
  IconButton,
  Grid,
} from "@chakra-ui/react";
import { FormTitle } from "components/form-title";
import { InputField } from "components/input-field";
import { DraggableFileField } from "components/input-file";
import { CampanhaWhatsAppProps } from "..";
import { DropdownField } from "components/dropdown-field";
import { IoDocumentText } from "react-icons/io5";
import { FaFileAlt, FaImage, FaVideo } from "react-icons/fa";
import { AiFillAudio } from "react-icons/ai";
import { useEffect, useState } from "react";
import api from "api/api";
import { MessageSquareIcon } from "components/vectors/message-square-icon";
import { FileIcon } from "components/vectors/file-icon";
import { SendIcon } from "components/vectors/send-icon";
import { ButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { CSVHelper } from "utils/csv-helper";
import { onChangeMultiselect } from "components/dropdown/utils";
import { Popover } from "components/popover";
import { PreviewMessage } from "./preview-message";
import { WhatsAppSession } from "components/atendimentos-components/whatsapp-attendances/components/whats-app-chat/hooks/use-whats-app-session-controls/types";
import {
  currencyFloatMaskFormat,
  limitNumberIntervalMask,
} from "utils/string-formats";

export function ModalCampanhaWhatsAppSteps({
  goToNext,
  goToPrevious,
  activeStep,
  onChangeField,
  fields,
  onSubmit,
}: {
  activeStep: number;
  goToNext: () => void;
  goToPrevious: () => void;
  onChangeField: (field: keyof CampanhaWhatsAppProps, value: any) => void;
  fields: Partial<CampanhaWhatsAppProps>;
  onSubmit: () => Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<
    { name: string; value: string }[]
  >([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [whatsAppPhones, setWhatsAppPhones] = useState<WhatsAppSession[]>([]);
  const [csvFirstLine, setCsvFirstLine] = useState<Record<string, any>>({});
  const whatsAppSessions = whatsAppPhones.map(({ id, nome }) => ({
    name: nome,
    value: id,
  }));

  const isDisabledNextButton =
    activeStep === 0
      ? !fields.name || !fields.contatoFiles
      : activeStep === 1
        ? !fields.type ||
          (fields.enabledMensagemSaudacao &&
            !fields.mensagemSaudacao?.trim()) ||
          (fields.type === "IMAGE" && !fields.midiaFiles?.[0]) ||
          (fields.type === "AUDIO" && !fields.midiaFiles?.[0]) ||
          (fields.type === "VIDEO" && !fields.midiaFiles?.[0]) ||
          (fields.type === "TEXT" && !fields.mensagemNormal?.trim())
        : activeStep === 2
          ? !Number(fields.qtdEstimadaEnvioDia || 0) ||
            !fields.wppSessionIds?.length ||
            !fields.fonesColunas?.length
          : activeStep === 3
            ? false
            : false;

  const handleFileCsv = async (file: File) => {
    onChangeField("contatoFiles", file);
    const csvHelper = new CSVHelper(file);
    const headers = await csvHelper.getHeaders();
    const json = await csvHelper.csvToJSON();

    const columnsTelefone =
      "TELEFONE_GOOGLE	TELEFONE_NUMERO_0	TELEFONE_NUMERO_1	TELEFONE_NUMERO_2	TELEFONE_NUMERO_3	TELEFONE_NUMERO_4	TELEFONE_NUMERO_5	TELEFONE_NUMERO_6	TELEFONE_NUMERO_7	TELEFONE_NUMERO_8	TELEFONE_NUMERO_9"
        .trim()
        .split("	");

    const validPhones = new Set<string>();
    json.forEach((curr) => {
      columnsTelefone.forEach((col) => {
        const phone = curr[col]?.trim();
        if (phone && phone.length >= 11) {
          validPhones.add(phone);
        }
      });
    });

    const data = {
      quantasLinhasTemTelefone: json.filter((curr) => {
        const rowPhones = new Set<string>();
        columnsTelefone.forEach((col) => {
          const phone = curr[col]?.trim();
          if (phone && phone.length >= 11) {
            rowPhones.add(phone);
          }
        });
        return rowPhones.size > 0;
      }).length,
      quantosTelefonesTemNoArquivo: json.reduce((total, curr) => {
        const rowPhones = [];
        columnsTelefone.forEach((col) => {
          const phone = (curr[col]?.trim() || "").replace(/\D/g, "");
          if (phone && phone.length >= 8) {
            rowPhones.push(phone);
          }
        });
        return total + rowPhones.length;
      }, 0),
    };
    console.log(data);

    setCsvFirstLine(json[0] || {});
    setCsvHeaders(
      headers
        .filter((h) => Boolean(h.trim()))
        .map((h) => ({ name: h, value: h })),
    );
  };

  const getPhones = async () => {
    setIsLoadingSessions(true);
    try {
      const { data } = await api.get("/whatsapp-sessions?only_disparo=true");
      setWhatsAppPhones(data);
    } catch (e) {
    } finally {
      setIsLoadingSessions(false);
    }
  };

  useEffect(() => {
    getPhones();
  }, []);

  const SubmitForm = async () => {
    setIsLoading(true);
    try {
      await onSubmit();
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const selectedSessionsLength = fields?.wppSessionIds?.length ?? 0;

  const whatsAppSessionsLabel =
    selectedSessionsLength > 1
      ? `${selectedSessionsLength} Participantes`
      : (whatsAppPhones.find(({ id }) => id === fields.wppSessionIds?.[0])
          ?.nome ?? "Selecione");

  return (
    <>
      <Box
        w="calc(100% + 24px + 24px)"
        mx="-24px"
        overflow="auto"
        px="24px"
        pt="10px"
        maxH="calc(100vh - 60px - 120px - 126px)"
      >
        {activeStep === 0 ? (
          <Box w="100%">
            <FormTitle mb="10px">Dados da campanha</FormTitle>
            <Text color="gray.500" mb="10px">
              Identifique sua campanha e configure o arquivo de contatos.
            </Text>
            <Box mb="10px">
              <InputField
                title="Nome da campanha"
                onChange={({ target: { value } }) =>
                  onChangeField("name", value)
                }
                value={fields.name}
              />
            </Box>
            <Box>
              <Text mb="8px" fontWeight="medium">
                Escolha a lista
              </Text>
              <DraggableFileField
                file={fields.contatoFiles}
                handleFile={handleFileCsv}
                error=""
              />
            </Box>
          </Box>
        ) : activeStep === 1 ? (
          <Box w="100%">
            <FormTitle mb="10px">Dados da Mensagem</FormTitle>
            <Text color="gray.500" mb="10px">
              Configure a mensagem que será enviada para seus contatos.
            </Text>
            <Box mb="10px">
              <Flex justifyContent="flex-start">
                <Switch
                  display="flex"
                  alignItems="center"
                  onChange={() =>
                    onChangeField(
                      "enabledMensagemSaudacao",
                      !fields.enabledMensagemSaudacao,
                    )
                  }
                  colorScheme="primary400"
                  isChecked={fields.enabledMensagemSaudacao}
                  mb="10px"
                >
                  <Text cursor="pointer">Adicionar Mensagem de Saudação</Text>
                </Switch>
              </Flex>
              {fields.enabledMensagemSaudacao && (
                <Box mb="10px">
                  <Flex
                    mb="8px"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="4px"
                  >
                    <Text fontWeight="medium">Mensagem de Saudação</Text>
                    <Popover
                      button={
                        <IconButton
                          aria-label=""
                          icon={<HamburgerIcon w="14px" h="14px" />}
                          size="xs"
                          variant="outline"
                        />
                      }
                      position="left"
                      popupStyles={{ width: "330px" }}
                    >
                      <Grid
                        templateColumns="1fr 1fr"
                        flexDir="column"
                        gap="6px"
                        w="100%"
                        maxH="200px"
                        overflow="auto"
                      >
                        {csvHeaders.map((header) => {
                          return (
                            <Button
                              size="xs"
                              key={header.value}
                              onClick={() => {
                                const currentValue =
                                  fields.mensagemSaudacao || "";
                                onChangeField(
                                  "mensagemSaudacao",
                                  currentValue + `#${header.value}#`,
                                );
                              }}
                            >
                              {header.value}
                            </Button>
                          );
                        })}
                      </Grid>
                    </Popover>
                  </Flex>

                  <Box mb="10px">
                    <InputField
                      onChange={(e) =>
                        onChangeField("mensagemSaudacao", e.target.value)
                      }
                      value={fields.mensagemSaudacao}
                      inputProps={{ as: "textarea", height: "80px", py: "6px" }}
                    />
                  </Box>
                  <PreviewMessage
                    message={fields.mensagemSaudacao || "Preview da Mensagem"}
                    headers={csvHeaders.map((header) => header.value)}
                    csvFirstLine={csvFirstLine}
                  />
                </Box>
              )}
            </Box>
            <SimpleGrid columns={5} spacing={2} mt="10px">
              {messageTypeOptions(onChangeField).map((option) => {
                const isActive = fields.type === option.value;
                return (
                  <Center
                    key={option.value}
                    onClick={() => {
                      onChangeField("type", option.value);
                      messageTypeOptions(onChangeField)
                        .filter(({ value }) => value !== option.value)
                        .forEach(({ onClose }) => onClose());
                    }}
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    p={3}
                    borderWidth="1px"
                    cursor="pointer"
                    borderColor={"custom-gray"}
                    bg={isActive ? "gray.200" : "transparent"}
                    color={isActive ? "primary.400" : "gray.500"}
                    _hover={{
                      bg: isActive ? "gray.200" : "gray.50",
                    }}
                    rounded="md"
                  >
                    <Box mb={1}>{option.icon}</Box>
                    <Text fontSize="xs" fontWeight="medium">
                      {option.label}
                    </Text>
                  </Center>
                );
              })}
            </SimpleGrid>
            {fields.type === "TEXT" ? (
              <Box mt="10px" mb="15px">
                <Flex justifyContent="flex-end">
                  <Popover
                    button={
                      <IconButton
                        aria-label=""
                        icon={<HamburgerIcon w="14px" h="14px" />}
                        size="xs"
                        variant="outline"
                        mb="10px"
                      />
                    }
                    position="left"
                    popupStyles={{ width: "330px" }}
                  >
                    <Grid
                      templateColumns="1fr 1fr"
                      flexDir="column"
                      gap="6px"
                      w="100%"
                      maxH="200px"
                      overflow="auto"
                    >
                      {csvHeaders.map((header) => {
                        return (
                          <Button
                            size="xs"
                            key={header.value}
                            onClick={() => {
                              const currentValue = fields.mensagemNormal || "";
                              onChangeField(
                                "mensagemNormal",
                                currentValue + `#${header.value}#`,
                              );
                            }}
                          >
                            {header.value}
                          </Button>
                        );
                      })}
                    </Grid>
                  </Popover>
                </Flex>
                <Box mb="10px">
                  <InputField
                    inputProps={{ as: "textarea", height: "108px", py: "6px" }}
                    onChange={(e) => {
                      onChangeField(
                        "mensagemNormal",
                        e.target.value.slice(0, 1800),
                      );
                    }}
                    value={fields.mensagemNormal}
                  />
                  <Text fontSize="xs" color="gray.500" mb="10px" mt="4px">
                    Máximo de 1800 caracteres.{" "}
                    {fields.mensagemNormal?.length ?? 0}
                    /1800
                  </Text>
                </Box>

                <PreviewMessage
                  message={fields.mensagemNormal || "Preview da Mensagem"}
                  headers={csvHeaders.map((header) => header.value)}
                  csvFirstLine={csvFirstLine}
                />
              </Box>
            ) : fields.type === "IMAGE" ? (
              <Box my="15px">
                <DraggableFileField
                  handleFile={(file) => {
                    onChangeField("midiaFiles", [file]);
                  }}
                  handleDelete={() => {
                    onChangeField("midiaFiles", undefined);
                  }}
                  file={fields.midiaFiles?.[0]}
                  error=""
                  fileExtensions={["image/jpeg", "image/jpg", "image/png"]}
                  fileSizeLimit={5120}
                />
              </Box>
            ) : fields.type === "AUDIO" ? (
              <Box my="15px">
                <DraggableFileField
                  handleFile={(file) => {
                    onChangeField("midiaFiles", [file]);
                  }}
                  handleDelete={() => {
                    onChangeField("midiaFiles", undefined);
                  }}
                  file={fields.midiaFiles?.[0]}
                  error=""
                  fileExtensions={["audio/mpeg", "audio/m4a", "audio/3gpp"]}
                  fileSizeLimit={10240}
                />
              </Box>
            ) : fields.type === "VIDEO" ? (
              <Box my="15px">
                <DraggableFileField
                  handleFile={(file) => {
                    onChangeField("midiaFiles", [file]);
                  }}
                  handleDelete={() => {
                    onChangeField("midiaFiles", undefined);
                  }}
                  file={fields.midiaFiles?.[0]}
                  error=""
                  fileExtensions={[
                    "video/mp4",
                    "video/3gpp",
                    "video/quicktime",
                  ]}
                  fileSizeLimit={16384}
                />
              </Box>
            ) : fields.type === "DOCUMENT" ? (
              <Box my="15px">
                <DraggableFileField
                  handleFile={(file) => {
                    onChangeField("midiaFiles", [file]);
                  }}
                  handleDelete={() => {
                    onChangeField("midiaFiles", undefined);
                  }}
                  file={fields.midiaFiles?.[0]}
                  error=""
                  fileExtensions={[]}
                  fileSizeLimit={16384}
                />
              </Box>
            ) : null}
          </Box>
        ) : activeStep === 2 ? (
          <Box w="100%">
            <FormTitle mb="10px">Configuração de Envio</FormTitle>
            <Text color="gray.500" mb="10px">
              Configure como sua campanha será enviada.
            </Text>

            <Box>
              <InputField
                title="Quantidade estimada por dia"
                onChange={(e) =>
                  onChangeField(
                    "qtdEstimadaEnvioDia",
                    e.target.value
                      ? limitNumberIntervalMask(0, 500)(e.target.value).value
                      : "",
                  )
                }
                value={
                  limitNumberIntervalMask(0, 500)(fields.qtdEstimadaEnvioDia)
                    .value
                }
                inputProps={{ type: "number" }}
              />
            </Box>
            <Text fontSize="xs" color="gray.500" mb="10px" mt="4px">
              Recomendado: entre 50 e 200 mensagens por dia para evitar
              bloqueios.
            </Text>
            <Box mb="10px">
              <DropdownField
                title="Coluna de Telefone"
                onChange={(value) =>
                  onChangeField(
                    "fonesColunas",
                    onChangeMultiselect(value, fields.fonesColunas),
                  )
                }
                value={fields.fonesColunas ?? []}
                options={csvHeaders}
                dropdownProps={{ multiSelect: true, openTop: true }}
              />
            </Box>
            <Box>
              <DropdownField
                title="Sessões whatsApp participantes"
                options={whatsAppSessions}
                onChange={(value) =>
                  onChangeField(
                    "wppSessionIds",
                    onChangeMultiselect(value, fields.wppSessionIds),
                  )
                }
                isLoading={isLoadingSessions}
                value={fields.wppSessionIds ?? []}
                dropdownProps={{
                  multiSelect: true,
                  children: whatsAppSessionsLabel,
                  openTop: true,
                }}
              />
            </Box>
          </Box>
        ) : activeStep === 3 ? (
          <Box w="100%">
            <FormTitle mb="10px">Revisar e Confirmar</FormTitle>
            <Box
              bg="gray.50"
              border="1px solid"
              borderColor="gray.100"
              rounded="lg"
              p={6}
              gap={4}
            >
              <VStack align="stretch" spacing={4}>
                <HStack align="start" spacing={3}>
                  <Box bg="blue.100" p={2} rounded="md">
                    <MessageSquareIcon color="blue.600" />
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="primary.400">
                      Dados da Campanha
                    </Text>
                    <VStack align="start" mt={2} spacing={1}>
                      <Text fontSize="sm" color="gray.600">
                        <Box as="span" fontWeight="medium">
                          Nome:
                        </Box>{" "}
                        {fields.name || "Não definido"}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        <Box as="span" fontWeight="medium">
                          Arquivo CSV:
                        </Box>{" "}
                        {fields.contatoFiles?.name || "Não selecionado"}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        <Box as="span" fontWeight="medium">
                          Coluna de Telefone:
                        </Box>{" "}
                        {fields.fonesColunas?.join(", ") || "Não selecionada"}
                      </Text>
                    </VStack>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Box bg="green.100" p={2} rounded="md">
                    <FileIcon color="green.600" />
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="primary.400">
                      Mensagem
                    </Text>
                    <VStack align="start" mt={2} spacing={1}>
                      <Text fontSize="sm" color="gray.600">
                        <Box as="span" fontWeight="medium">
                          Tipo:
                        </Box>{" "}
                        {messageTypeOptions().find(
                          ({ value }) => value === fields?.type,
                        )?.label || "Não definido"}
                      </Text>
                      {fields.enabledMensagemSaudacao && (
                        <Text fontSize="sm" color="gray.600">
                          <Box as="span" fontWeight="medium">
                            Saudação:
                          </Box>{" "}
                          {fields.mensagemSaudacao || "Padrão"}
                        </Text>
                      )}
                      {fields.type === "TEXT" && (
                        <Text fontSize="sm" color="gray.600">
                          <Box as="span" fontWeight="medium">
                            Conteúdo:
                          </Box>{" "}
                          {fields.mensagemNormal || "Não definido"}
                        </Text>
                      )}
                      {messageTypeOptions()
                        .map(({ value }) => value as string)
                        .filter((value) => value != null && value !== "TEXT")
                        .includes(fields.type!) && (
                        <Text fontSize="sm" color="gray.600">
                          <Box as="span" fontWeight="medium">
                            Arquivo:
                          </Box>{" "}
                          {fields.midiaFiles?.[0]?.name || "Não selecionado"}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Box bg="purple.100" p={2} rounded="md">
                    <SendIcon width="20px" height="20px" color="purple.600" />
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="primary.400">
                      Configuração de Envio
                    </Text>

                    <VStack align="start" mt={2} spacing={1}>
                      <Text fontSize="sm" color="gray.600">
                        <Box as="span" fontWeight="medium">
                          Quantidade por dia:
                        </Box>{" "}
                        {fields.qtdEstimadaEnvioDia || "Não definido"}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        <Box as="span" fontWeight="medium">
                          WhatsApp participantes:
                        </Box>{" "}
                        {fields.wppSessionIds?.length || 0} selecionados
                      </Text>
                    </VStack>
                  </Box>
                </HStack>
                <Box
                  mt={4}
                  p={4}
                  bg="gray.100"
                  borderWidth={1}
                  borderColor="primary.300"
                  rounded="md"
                  display="flex"
                  alignItems="center"
                >
                  <CheckCircleIcon
                    w="20px"
                    h="20px"
                    color="primary.400"
                    style={{ marginRight: 8, flexShrink: 0 }}
                  />
                  <Text fontSize="sm" color="primary.400">
                    Sua campanha está pronta para ser salva. Clique em "Salvar e
                    Enviar" para continuar.
                  </Text>
                </Box>
              </VStack>
            </Box>
          </Box>
        ) : null}
      </Box>
      <Flex justifyContent="space-between" w="100%" pb="8px" pt="16px">
        <Button
          onClick={() => goToPrevious()}
          variant="outline"
          mr="10px"
          leftIcon={<ArrowLeftIcon w="12px" h="12px" />}
          isDisabled={activeStep === 0 || isLoading}
        >
          Voltar
        </Button>
        <Button
          onClick={async () => {
            if (activeStep === 3) {
              await SubmitForm();
              return;
            }
            goToNext();
          }}
          isLoading={isLoading}
          variant="outline"
          rightIcon={<ArrowRightIcon w="12px" h="12px" />}
          isDisabled={isDisabledNextButton}
          loadingText={activeStep === 3 ? "Salvando" : "Carregando"}
        >
          {activeStep === 3 ? "Salvar e Enviar" : "Próximo"}
        </Button>
      </Flex>
    </>
  );
}

export const messageTypeOptions = (
  onChangeField?: (field: keyof CampanhaWhatsAppProps, value: any) => void,
): {
  value: CampanhaWhatsAppProps["type"];
  label: string;
  icon: JSX.Element;
  onClose: () => void;
}[] => [
  {
    value: "TEXT",
    label: "Texto",
    icon: <IoDocumentText size={16} />,
    onClose: () => onChangeField?.("mensagemNormal", undefined),
  },
  {
    value: "IMAGE",
    label: "Imagem",
    icon: <FaImage size={16} />,
    onClose: () => onChangeField?.("midiaFiles", undefined),
  },
  {
    value: "AUDIO",
    label: "Áudio",
    icon: <AiFillAudio size={16} />,
    onClose: () => onChangeField?.("midiaFiles", undefined),
  },
  {
    value: "VIDEO",
    label: "Vídeo",
    icon: <FaVideo size={16} />,
    onClose: () => onChangeField?.("midiaFiles", undefined),
  },
  {
    value: "DOCUMENT",
    label: "Documento",
    icon: <FaFileAlt size={16} />,
    onClose: () => onChangeField?.("midiaFiles", undefined),
  },
];
